import { ComboboxItem } from '@node-space/ui-components'
import { Currencies } from 'types/types'
import { getTranslation } from '../../../../__mocks__/i18nextMock'
import { RISK_SCORES, USE_CASES } from './constants'
import {
  BusinessCustomerFormData,
  BusinessCustomerFormKey,
  EmbeddedCustomerBaseData,
  IndividualCustomerFormData,
  IndividualCustomerFormKey,
  RiskScore,
  UseCase,
} from './types'

export type FormKeyMapping<MappingType> = Partial<Record<IndividualCustomerFormKey, MappingType>>

export const BASE_CUSTOMER_DEFAULT_FORM_DATA: EmbeddedCustomerBaseData = {
  riskScore: '' as RiskScore,
  useCase: '' as UseCase,
  externalReference: '',
}

export const BUSINESS_CUSTOMER_DEFAULT_FORM_DATA: BusinessCustomerFormData = {
  businessName: '',
  businessDescription: '',
  industry: '',
  countryOfIncorporation: '',
  monthlyExpectedVolumes: '',
  ...BASE_CUSTOMER_DEFAULT_FORM_DATA,
}

export const BASE_CUSTOMER_OPTIONS: FormKeyMapping<ComboboxItem[]> = {
  riskScore: Object.values(RISK_SCORES)?.map(riskScore => ({
    leftText: getTranslation(riskScore?.toLowerCase()),
    value: riskScore,
  })),
  useCase: Object.values(USE_CASES)?.map(useCase => ({
    leftText: getTranslation(`wallets.${useCase?.toLowerCase()}`),
    value: useCase,
  })),
}

export const INDIVIDUAL_CURRENCIES = [Currencies.USD, Currencies.EUR]

export const INDIVIDUAL_DESCRIPTION = {
  transfersOwnWallet: 'Transfers to/from Own Wallet (including Bank Accounts)',
  transfersFamilyAndFriends: 'Transfers to/from Family & Friends',
  investments: 'Investments',
  goodsServicesPurchases: 'Goods & Services Purchases',
  donations: 'Donations',
}

export const INDIVIDUAL_EMPLOYMENT_STATUS = {
  selfEmployedFreelancer: 'Self-employed/Freelancer',
  salaried: 'Salaried',
  unemployed: 'Unemployed',
  retired: 'Retired',
  notProvided: 'Not Provided',
}

export const INDIVIDUAL_PEP_STATUS = {
  notAPep: 'Not a PEP',
  formerInactiveWithinTwoYears: 'Former/Inactive PEP (within 2 years)',
  formerInactiveOlderTwoYears: 'Former/Inactive PEP (older than 2 years)',
  domestic: 'Domestic PEP',
  foreign: 'Foreign PEP',
  closeAssociates: 'Close Associates',
  familyMembers: 'Family Members',
}

export const INDIVIDUAL_SOURCE_OF_FUNDS = {
  salary: 'Salary/Employment Income',
  pension: 'Pension/Retirement Income',
  savingsInvestments: 'Savings & Investments',
  selfEmployed: 'Self-Employment/Business Income',
  cryptoTrading: 'Crypto Trading',
  gambling: 'Gambling/Lottery Winnings',
  realEstate: 'Real Estate Sales',
}

export const INDIVIDUAL_CUSTOMER_DEFAULT_FORM_DATA: IndividualCustomerFormData = {
  ...BASE_CUSTOMER_DEFAULT_FORM_DATA,
  firstName: '',
  lastName: '',
  dateOfBirth: '',
  emailAddress: '',
  description: '',
  nationality: '',
  addressLine1: '',
  addressLine2: '',
  city: '',
  postalCode: '',
  countryCode: '',
  stateCode: '',
  taxIdentificationNumber: '',
  taxResidenceCountryCode: '',
  expectedMonthlyVolumeAmount: '',
  expectedMonthlyVolumeCurrency: INDIVIDUAL_CURRENCIES?.[0],
  employmentStatus: '',
  sourceOfFunds: '',
  pepStatus: '',
}

export const BASE_CUSTOMER_LABEL_MAPPING: Partial<Record<keyof EmbeddedCustomerBaseData, string>> =
  {
    useCase: 'customers.serviceType',
    externalReference: 'customers.externalReference',
  }

export const INDIVIDUAL_CUSTOMER_LABEL_MAPPING: FormKeyMapping<string> = {
  ...BASE_CUSTOMER_LABEL_MAPPING,
  emailAddress: 'formFields.email',
  nationality: 'customers.countryOfNationality',
  postalCode: 'customers.address.postalCode',
  countryCode: 'customers.address.country',
  stateCode: 'customers.address.state',
  taxIdentificationNumber: 'customers.taxIdentification',
  taxResidenceCountryCode: 'customers.taxResidency',
  expectedMonthlyVolumeAmount: 'customers.monthlyVolumes',
  expectedMonthlyVolumeCurrency: 'formFields.currency',
  employmentStatus: 'customers.employmentStatus',
  pepStatus: 'customers.pepStatus',
  sourceOfFunds: 'customers.sourceOfFunds',
  riskScore: 'embeddedCustomers.businessForm.riskScore',
  description: 'customers.intendedUse',
}

export const BUSINESS_CUSTOMER_LABEL_MAPPING: Partial<Record<BusinessCustomerFormKey, string>> = {
  ...BASE_CUSTOMER_LABEL_MAPPING,
}
